/**
 * Lightweight client-side lead scoring.
 *
 * Produces a 0-100 score and a Hot / Warm / Cold tier so reps can instantly
 * see which enquiries deserve attention first. Scoring is intentionally
 * heuristic and derived only from data already present on a lead, so it works
 * without any backend/schema changes.
 */

const HOT_SOURCES = ["google", "ads", "website", "referral", "facebook", "instagram", "whatsapp"];

const toNumber = (value) => {
    if (value === null || value === undefined) return 0;
    const cleaned = String(value).replace(/[^0-9.]/g, "");
    const num = Number(cleaned);
    return Number.isFinite(num) ? num : 0;
};

const hoursSince = (dateLike) => {
    if (!dateLike) return Infinity;
    const ts = Date.parse(dateLike);
    if (Number.isNaN(ts)) return Infinity;
    return (Date.now() - ts) / (1000 * 60 * 60);
};

/**
 * @returns {{ score: number, tier: 'Hot'|'Warm'|'Cold'|'Won'|'Lost', reasons: string[] }}
 */
export const getLeadScore = (lead = {}) => {
    const status = String(lead.extra || "").toLowerCase();

    // Closed states short-circuit so they read clearly on the board.
    if (status === "converted") {
        return { score: 100, tier: "Won", reasons: ["Converted to customer"] };
    }
    if (status === "not interested") {
        return { score: 0, tier: "Lost", reasons: ["Marked not interested"] };
    }

    let score = 0;
    const reasons = [];

    // 1. Recency of the enquiry — fresh leads convert far better.
    const ageHours = hoursSince(lead.createdAt || lead.created_at);
    if (ageHours <= 24) {
        score += 30;
        reasons.push("New (<24h)");
    } else if (ageHours <= 72) {
        score += 20;
        reasons.push("Recent (<3d)");
    } else if (ageHours <= 168) {
        score += 10;
        reasons.push("This week");
    }

    // 2. Source quality.
    const source = `${lead.source || ""} ${lead.enquiry_source || ""}`.toLowerCase();
    if (HOT_SOURCES.some((s) => source.includes(s))) {
        score += 15;
        reasons.push("High-intent source");
    }

    // 3. Engagement signals.
    if (lead.connected_time) {
        score += 15;
        reasons.push("Already connected");
    }
    if (Number(lead.noteCount) > 0) {
        score += 10;
        reasons.push("Has notes");
    }
    if (lead.star === true || lead.star === "true" || lead.star === 1 || lead.star === "1") {
        score += 10;
        reasons.push("Starred");
    }

    // 4. Intent value (loan / borrow amount).
    const value = toNumber(lead.borrowAmount);
    if (value >= 500000) {
        score += 15;
        reasons.push("High value");
    } else if (value >= 100000) {
        score += 10;
        reasons.push("Good value");
    } else if (value > 0) {
        score += 5;
    }

    // 5. Stage progress.
    if (status === "in progress") {
        score += 10;
        reasons.push("In progress");
    } else if (status === "pending") {
        score += 5;
    }

    // 6. Business customers tend to be higher value.
    if (String(lead.type_customer || "").toLowerCase() === "business") {
        score += 5;
    }

    score = Math.max(0, Math.min(100, score));

    let tier = "Cold";
    if (score >= 60) tier = "Hot";
    else if (score >= 30) tier = "Warm";

    return { score, tier, reasons };
};

/** Tailwind-friendly colour tokens per tier for badges. */
export const TIER_STYLES = {
    Hot: { label: "Hot", className: "bg-red-100 text-red-700 border-red-200", dot: "#ef4444" },
    Warm: { label: "Warm", className: "bg-amber-100 text-amber-700 border-amber-200", dot: "#f59e0b" },
    Cold: { label: "Cold", className: "bg-sky-100 text-sky-700 border-sky-200", dot: "#0ea5e9" },
    Won: { label: "Won", className: "bg-emerald-100 text-emerald-700 border-emerald-200", dot: "#10b981" },
    Lost: { label: "Lost", className: "bg-gray-100 text-gray-500 border-gray-200", dot: "#9ca3af" },
};

export default getLeadScore;
